import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { AccessLevel, AccountMarketplace, AmazonUser, DspAdvertiser, Marketplace } from "@front/m19-api-client";
import { Marketplaces } from "@front/m19-models";
import { AccountMarketplaceService, AccountSelectionService, AmazonUserService } from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonDirective,
  IIconComponent,
  IInputDirective,
  ILabelComponent,
  ILoginWithAmazonComponent,
  IRadioComponent,
  IRadioGroupComponent,
  ITooltipDirective,
  MarketplaceFlagComponent,
  MODAL_DATA,
  ModalOptions,
  ModalRef,
  SpinnerComponent,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { map, take } from "rxjs/operators";

type DspAdvertiserWithSeat = DspAdvertiser & { onM19Seat: boolean };

@Component({
  selector: "app-link-dsp-modal",
  template: ` <div class="flex h-full flex-col gap-2" *transloco="let t">
    @let advertisers = dspAdvertisers();
    @if (loading()) {
      <div class="flex w-full flex-1 items-center justify-center">
        <ISpinner [display]="true" />
      </div>
    } @else if (advertisers === undefined || advertisers.length === 0 || errorMessage()) {
      @if (errorMessage() || advertisers !== undefined) {
        <div class="p-2">
          <div class="border-l-4 border-orange-500 bg-orange-100 p-4 text-orange-700">
            {{ errorMessage() ?? t("link-dsp.no_dsp_advertisers_found_retry_amazon_login") }}
          </div>
        </div>
      }
      <div class="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
        <div class="text-gray-700">
          {{ t("link-dsp.login_with_amazon_to_link_your_dsp_advertiser") }}
        </div>
        <ILoginWithAmazon (loginWithAmazon)="logWithAmazon()" />
      </div>
    } @else {
      <div class="mt-4 text-center text-gray-700">
        {{
          advertisers.length === 1
            ? t("link-dsp.advertiser_link_message", { name: advertisers[0].name })
            : t("link-dsp.select_the_dsp_advertiser_you_want_to_link")
        }}
      </div>

      <div class="flex flex-1 flex-col overflow-y-auto">
        @if (advertisers.length > 1) {
          <div class="sticky top-0 z-10 bg-white p-2 shadow-xs">
            <input iInput [(ngModel)]="filter" [placeholder]="t('link-dsp.search_by_advertiser_name')" />
          </div>
        }
        <div class="flex flex-col p-2">
          <IRadioGroup
            class="flex flex-col gap-4"
            name="dspAdvertiserToLink"
            [value]="selectedDspAdvertiser()"
            (valueChange)="selectDspAdvertiser($event)"
          >
            @for (dspAdvertiser of filteredDspAdvertisers(); track dspAdvertiser.dspAdvertiserId) {
              @let radioId = "dsp_" + dspAdvertiser.dspAdvertiserId;
              <label iLabel class="cursor-pointer" [for]="radioId" type="card">
                <div class="flex flex-col gap-4">
                  <div class="flex items-center gap-3">
                    <a [href]="dspAdvertiser.url" target="_blank">
                      <div
                        class="text-main-500 flex items-center gap-2 font-semibold"
                        [iTooltip]="t('link-dsp.see_advertiser_website')"
                      >
                        {{ dspAdvertiser.name }}
                        <IIcon name="link" />
                      </div>
                    </a>
                  </div>
                  <div class="flex justify-between">
                    <div class="flex items-center gap-4">
                      @if (dspAdvertiser.onM19Seat) {
                        <IBadge color="main" size="xs" [label]="t('common.m19_seat')"></IBadge>
                      }
                      @if (dspAdvertiser.isRegional) {
                        <IBadge
                          color="gray"
                          size="xs"
                          [label]="t('link-dsp.global_platform', { platform: dspAdvertiser.platform })"
                        ></IBadge>
                      } @else {
                        <IMarketplaceFlag [marketplace]="dspAdvertiser.marketplace!"></IMarketplaceFlag>
                      }
                      <div class="text-xs text-gray-600">{{ t("link-dsp.currency") }} {{ dspAdvertiser.currency }}</div>
                    </div>
                    <div class="self-end text-xs text-gray-400">ID: {{ dspAdvertiser.dspAdvertiserId }}</div>
                  </div>
                </div>
                <IRadio [id]="radioId" [value]="dspAdvertiser" size="lg" />
              </label>
            } @empty {
              <div class="mt-4 flex justify-center text-sm text-gray-600">
                {{ t("link-dsp.no_dsp_advertisers_found") }}
              </div>
            }
          </IRadioGroup>
        </div>
      </div>
    }

    @let marketplacesToUpdate = accountMarketplacesToUpdate();
    @if (marketplacesToUpdate?.length) {
      <div class="flex flex-col gap-2 border-t p-4 text-sm text-gray-700">
        <div
          [innerHTML]="
            t('link-dsp.marketplaces_to_link_message', {
              accountId: modalData.accountMarketplace.accountId,
              dspAdvertiserName: selectedDspAdvertiser()!.name,
            })
          "
        ></div>
        <div class="ml-2 flex flex-wrap items-center gap-3">
          @for (am of marketplacesToUpdate; track am.marketplace) {
            <div class="flex items-center gap-1">
              <IMarketplaceFlag [marketplace]="am.marketplace"></IMarketplaceFlag>
              <span>{{ am.marketplace }}</span>
            </div>
          }
        </div>
      </div>
    }

    <div class="flex justify-end gap-4">
      <button iButton variant="secondary" [disabled]="isLinkingDspAdvertiser()" (click)="modalRef.close()">
        {{ t("common.cancel") }}
      </button>
      @if (selectedDspAdvertiser()) {
        <button iButton [loading]="isLinkingDspAdvertiser()" (click)="save()">
          {{ t("common.confirm") }}
        </button>
      }
    </div>
  </div>`,
  imports: [
    TranslocoDirective,
    SpinnerComponent,
    IBadgeComponent,
    IButtonDirective,
    IInputDirective,
    FormsModule,
    ITooltipDirective,
    IIconComponent,
    MarketplaceFlagComponent,
    ILoginWithAmazonComponent,
    IRadioGroupComponent,
    ILabelComponent,
    IRadioComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkDspModalComponent {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "md", height: "fill" };

  readonly modalData = inject<LinkDspModalData>(MODAL_DATA);
  readonly modalRef = inject(ModalRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly amazonUserService = inject(AmazonUserService);
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);

  readonly Marketplaces = Marketplaces;
  readonly loading = signal(false);
  readonly filter = signal("");
  readonly dspAdvertisers = signal<DspAdvertiserWithSeat[] | undefined>(undefined);
  readonly filteredDspAdvertisers = computed<DspAdvertiserWithSeat[]>(() => {
    return (
      this.dspAdvertisers()?.filter((advertiser) =>
        advertiser.name.toLowerCase().includes(this.filter().toLowerCase()),
      ) ?? []
    );
  });
  readonly isLinkingDspAdvertiser = signal(false);

  readonly selectedDspAdvertiser = signal<DspAdvertiserWithSeat | undefined>(undefined);
  readonly errorMessage = signal<string | undefined>(undefined);
  readonly alreadyLinkedDspAdvertisers = toSignal(
    this.accountMarketplaceService.accountMarketplaces$.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((accountMarketplaces) =>
        accountMarketplaces
          .filter(
            (am) =>
              am.resourceOrganizationId === this.modalData.accountMarketplace.resourceOrganizationId &&
              am.marketplace === this.modalData.accountMarketplace.marketplace &&
              am.dspAdvertiserId !== undefined,
          )
          .map((am) => am.dspAdvertiserId!),
      ),
    ),
  );
  readonly organizationAccountMarketplaces = toSignal(
    this.accountSelectionService.selectedOrganizationAccountMarketplaces$,
  );

  readonly accountMarketplacesToUpdate = computed<AccountMarketplace[] | undefined>(() => {
    const selectedDspAdvertiser = this.selectedDspAdvertiser();
    if (!selectedDspAdvertiser) return undefined;

    const selectedAccountMarketplace = this.modalData.accountMarketplace;

    if (selectedDspAdvertiser.isRegional) {
      return (this.organizationAccountMarketplaces() ?? []).filter(
        (am) =>
          !am.dspAdvertiserId &&
          am.accessLevel === AccessLevel.ADMIN &&
          am.accountId === selectedAccountMarketplace.accountId &&
          am.resourceOrganizationId === selectedAccountMarketplace.resourceOrganizationId &&
          Marketplaces[am.marketplace].platform === selectedDspAdvertiser.platform,
      );
    }
    return [selectedAccountMarketplace];
  });

  logWithAmazon(): void {
    this.loading.set(true);
    this.selectedDspAdvertiser.set(undefined);
    const alreadyLinkedDspAdvertiserIds = this.alreadyLinkedDspAdvertisers() ?? [];
    this.amazonUserService
      .getAmazonUser(this.modalData.accountMarketplace.resourceOrganizationId!)
      .pipe(take(1))
      .subscribe({
        next: (amazonUser: AmazonUser) => {
          this.loading.set(false);
          const marketplaceEx = Marketplaces[this.modalData.accountMarketplace.marketplace as Marketplace];
          const marketplacePlatform = marketplaceEx.platform;
          const dspAdvertisers = (
            amazonUser.dspAdvertisers?.map<DspAdvertiserWithSeat>((d) => ({
              ...d,
              onM19Seat: AccountMarketplaceService.isOnM19Seat(d),
            })) ?? []
          ).filter((dspAdvertiser) => {
            // global advertiser -> check if the platform matches
            // non-global advertiser -> check if the marketplace matches
            // in any case, check if the advertiser is not already linked to another account
            return (
              ((dspAdvertiser.isRegional && dspAdvertiser.platform === marketplacePlatform) ||
                (!dspAdvertiser.isRegional &&
                  dspAdvertiser.marketplace === this.modalData.accountMarketplace.marketplace)) &&
              !alreadyLinkedDspAdvertiserIds.includes(dspAdvertiser.dspAdvertiserId)
            );
          });
          this.dspAdvertisers.set(dspAdvertisers);
          if (dspAdvertisers.length > 0) {
            this.selectedDspAdvertiser.set(dspAdvertisers[0]);
          }
        },
        error: (errorMessage: string) => {
          this.loading.set(false);
          this.toastrService.error(errorMessage);
          this.errorMessage.set(errorMessage);
        },
      });
  }

  selectDspAdvertiser(dspAdvertiser: DspAdvertiserWithSeat | undefined): void {
    if (!dspAdvertiser) return;
    this.selectedDspAdvertiser.set(dspAdvertiser);
  }

  save(): void {
    const selectedDspAdvertiser = this.selectedDspAdvertiser();
    const accountMarketplacesToUpdate = this.accountMarketplacesToUpdate();
    if (!selectedDspAdvertiser || !accountMarketplacesToUpdate) {
      return;
    }
    this.isLinkingDspAdvertiser.set(true);

    this.accountMarketplaceService
      .linkDspAdvertiser(accountMarketplacesToUpdate, selectedDspAdvertiser.dspAdvertiserId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLinkingDspAdvertiser.set(false);
          this.toastrService.success(this.translocoService.translate("link-dsp.dsp_advertiser_linked_successfully"));
          this.modalRef.close(selectedDspAdvertiser);
        },
        error: (error) => {
          this.isLinkingDspAdvertiser.set(false);
          this.errorMessage.set(error);
          this.toastrService.error(error, this.translocoService.translate("link-dsp.error_linking_dsp_advertiser"));
        },
      });
  }
}

export interface LinkDspModalData {
  accountMarketplace: AccountMarketplace;
}
